import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import Container from '../../components/shared/Container';
import { useMenuDrawer } from '../../components/shared/MenuDrawerProvider';
import { useHeader } from '../../context/HeaderContext';
import { getMenuCategories, getAllMenuItems, getMenuItemBySlug, getRelatedMenuItems, testSanityConnection } from '../../lib/sanity';
import SEO from '../../components/shared/SEO';
import StructuredData from '../../components/shared/StructuredData';
import CategorySubNav from '../../components/shared/CategorySubNav';
import MenuHeroSlideshow from '../../components/shared/MenuHeroSlideshow';
import '../../components/shared/CategorySubNav.css';
import { generateBreadcrumbSchema } from '../../utils/schema';
import { getSanityImageProps } from '../../utils/sanityImage';

// Helper function to extract plain text from portable text
const extractPlainText = (portableText) => {
  if (!portableText) return '';
  if (typeof portableText === 'string') return portableText;
  if (Array.isArray(portableText)) {
    return portableText
      .map(block => {
        if (block._type === 'block' && block.children) {
          return block.children.map(child => child.text).join('');
        }
        return '';
      })
      .join(' ');
  }
  return '';
};

export default function MenuPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUseBlurredBackground } = useHeader();
  const [menuCategories, setMenuCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use the drawer context instead of local state
  const { 
    openDrawer, 
    closeDrawer, 
    startLoading, 
    setError: setDrawerError, 
    finishLoading,
    getItemFromCache,
    getRelatedFromCache
  } = useMenuDrawer();

  // Function to handle opening the drawer with a menu item
  const openMenuItemDrawer = useCallback(async (itemSlug, categorySlug) => {
    try {
      // Start loading state
      startLoading();
      
      // Open the drawer immediately with loading state
      openDrawer(null, categorySlug, [], itemSlug);
      
      // Check if we already have this item in cache
      const cachedItem = getItemFromCache(itemSlug);
      
      if (cachedItem) {
        // Use cached item data
        const cachedRelated = getRelatedFromCache(cachedItem._id) || [];
        openDrawer(cachedItem, categorySlug, cachedRelated, itemSlug);
        finishLoading();
        return;
      }
      
      // Fetch the menu item data
      const item = await getMenuItemBySlug(itemSlug);
      
      if (!item) {
        setDrawerError('Menu item not found');
        return;
      }
      
      // Fetch related items
      let related = [];
      if (item.category && item.category._id) {
        related = await getRelatedMenuItems(item._id, item.category._id, 2) || [];
      }
      
      // Update the drawer with the fetched data
      openDrawer(item, categorySlug, related, itemSlug);
    } catch (err) {
      console.error(`Error fetching menu item ${itemSlug}:`, err);
      setDrawerError('Failed to load menu item. Please try again later.');
    } finally {
      finishLoading();
    }
  }, [startLoading, setDrawerError, openDrawer, finishLoading, getItemFromCache, getRelatedFromCache]);
  
  // Function to close the drawer
  const closeMenuItemDrawer = useCallback(() => {
    closeDrawer();
    
    // Also update URL directly in case the provider's method fails
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('item')) {
      searchParams.delete('item');
      const newUrl = `${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
      navigate(newUrl, { replace: true });
    }
  }, [closeDrawer, location, navigate]);
  
  // Enable blurred background for header on menu page
  useEffect(() => {
    setUseBlurredBackground(true);
    
    // Clean up when component unmounts
    return () => {
      setUseBlurredBackground(false);
    };
  }, [setUseBlurredBackground]);
  
  // Check URL for menu item on initial load or URL change
  useEffect(() => {
    // Get search parameters
    const searchParams = new URLSearchParams(location.search);
    const itemSlug = searchParams.get('item');
    
    // If we have an item parameter, open the drawer
    if (itemSlug) {
      // Get the category from the URL or use a default
      const pathParts = location.pathname.split('/');
      const categorySlug = pathParts.length >= 3 && pathParts[1] === 'menu' ? pathParts[2] : 'all';
      
      // Only fetch if we haven't already fetched this data
      if (!dataFetchedRef.current) {
        dataFetchedRef.current = true;
        openMenuItemDrawer(itemSlug, categorySlug);
      }
    } else {
      // Reset the data fetched flag when there's no item in the URL
      dataFetchedRef.current = false;
    }
  }, [location.search, location.pathname, openMenuItemDrawer]);

  // Use a ref to track if data has been loaded to prevent multiple fetches
  const dataFetchedRef = useRef(false);
  
  useEffect(() => {
    // Skip if data has already been fetched
    if (dataFetchedRef.current) return;
    
    async function fetchMenuData() {
      try {
        console.log('Menu page: Starting to fetch data...');
        setLoading(true);
        
        // First test the Sanity connection
        console.log('Menu page: Testing Sanity connection...');
        const testResult = await testSanityConnection();
        console.log('Menu page: Sanity connection test result:', testResult);
        
        if (!testResult.success) {
          console.error('Menu page: Sanity connection test failed:', testResult.error);
          setError(`Failed to connect to Sanity: ${testResult.error}`);
          return;
        }
        
        // Fetch categories first to debug
        console.log('Menu page: Fetching categories...');
        const categories = await getMenuCategories();
        console.log('Menu page: Categories response:', categories);
        
        if (categories && categories.length > 0) {
          console.log(`Menu page: Found ${categories.length} categories`);
          setMenuCategories(categories);
        } else {
          console.warn('Menu page: No menu categories returned from Sanity');
        }
        
        // Then fetch menu items
        console.log('Menu page: Fetching menu items...');
        const items = await getAllMenuItems();
        console.log('Menu page: Menu items response:', items);
        
        if (items && items.length > 0) {
          console.log(`Menu page: Found ${items.length} menu items`);
          setMenuItems(items);
        } else {
          console.warn('Menu page: No menu items returned from Sanity');
        }
        
        setError(null);
        
        // Mark data as fetched
        dataFetchedRef.current = true;
      } catch (err) {
        console.error('Menu page: Error fetching menu data:', err);
        setError('Failed to load menu. Please try again later.');
      } finally {
        console.log('Menu page: Finished fetching data, loading state set to false');
        setLoading(false);
      }
    }

    fetchMenuData();
  }, []);

  // Group menu items by category
  console.log('Menu items before grouping:', menuItems);
  
  const menuItemsByCategory = menuItems.reduce((acc, item) => {
    console.log('Processing item:', item);
    // Try to get category ID from either categoryId or category._id
    const categoryId = item.categoryId || (item.category && item.category._id);
    
    if (categoryId) {
      console.log('Item has category with ID:', categoryId);
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push(item);
    } else {
      console.warn('Item missing category ID:', item);
    }
    return acc;
  }, {});
  
  console.log('Menu items grouped by category:', menuItemsByCategory);
  
  // Log categories for debugging
  console.log('Categories for rendering:', menuCategories.map(cat => ({
    id: cat._id,
    name: cat.name,
    hasItems: menuItemsByCategory[cat._id] ? menuItemsByCategory[cat._id].length : 0
  })));

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  if (loading) {
    return (
      <div className="bg-brand-cream min-h-screen">
        {/* Hero Section Skeleton */}
        <div className="relative h-[400px] bg-gray-200 animate-pulse">
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-300 to-transparent"></div>
          
          <Container className="relative h-full">
            <div className="absolute bottom-8 left-0 right-0 w-full">
              <div className="flex flex-col md:flex-row md:justify-between md:items-end">
                {/* Left side - Title */}
                <div className="mb-4 md:mb-0">
                  <div className="h-10 w-48 bg-gray-300 rounded"></div>
                </div>
                
                {/* Right side - Description */}
                <div className="md:max-w-xl md:text-right">
                  <div className="h-4 bg-gray-300 rounded w-full"></div>
                  <div className="h-4 bg-gray-300 rounded w-5/6 mt-1"></div>
                </div>
              </div>
            </div>
          </Container>
        </div>
        
        {/* Menu Categories Skeleton */}
        <div className="bg-white py-16">
          <Container>
            {/* Category 1 */}
            <div className="mb-20">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Category Header */}
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-48 mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 mt-1"></div>
                </div>
                
                {/* Menu Items Grid */}
                <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse">
                      <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                      <div className="h-3 bg-gray-200 rounded w-5/6 mt-1"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Category 2 */}
            <div className="mb-20">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Category Header */}
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-40 mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/6 mt-1"></div>
                </div>
                
                {/* Menu Items Grid */}
                <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse">
                      <div className="h-5 bg-gray-200 rounded w-2/3 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4 mt-1"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-brand-cream min-h-screen">
        <Container>
          <div className="py-32 text-center">
            <h1 className="text-3xl font-heading text-brand-navy mb-4">Menu Unavailable</h1>
            <p className="text-gray-700 mb-6">{error}</p>
            <Link to="/" className="text-brand-navy underline">Return to Home</Link>
          </div>
        </Container>
      </div>
    );
  }

  console.log('Rendering menu page with:', { menuCategories, menuItems, menuItemsByCategory });
  
  // Get search parameters for SEO
  const searchParams = new URLSearchParams(location.search);
  const itemSlug = searchParams.get('item');
  const { selectedItem } = useMenuDrawer();
  
  // Determine page title and description based on selected item
  const pageTitle = selectedItem 
    ? `${selectedItem.name} | Menu | Original Pancake House` 
    : 'Menu | The Original Pancake House - Breakfast & Brunch';
  
  const pageDescription = selectedItem
    ? extractPlainText(selectedItem.description) || 'Explore our delicious menu at Original Pancake House.'
    : 'Explore our full menu of made-from-scratch breakfast and brunch favorites. From fluffy pancakes and Belgian waffles to omelets and crepes, discover handcrafted dishes with fresh ingredients at The Original Pancake House.';
  
  return (
    <div className="bg-brand-cream min-h-screen">
      <SEO 
        title={pageTitle}
        description={pageDescription}
        openGraph={selectedItem ? {
          type: 'article',
          image: selectedItem.imageUrl || "/oph-social-share2.jpg"
        } : {
          image: "/oph-social-share2.jpg"
        }}
      />
      
      {/* Structured Data for SEO */}
      <StructuredData data={generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Menu', url: '/menu' }
      ])} />
      
      {/* Hero Section */}
      <MenuHeroSlideshow categories={menuCategories} />

      {/* Category Sub Navigation */}
      <CategorySubNav categories={menuCategories} />

      {/* Menu Categories */}
      <div className="bg-white py-16">
        <Container>
          {menuCategories.map((category) => {
            // Get items for this category and sort them by order (if available)
            let categoryItems = menuItemsByCategory[category._id] || [];
            
            // Sort items: first by order (if available), then alphabetically by name
            categoryItems = [...categoryItems].sort((a, b) => {
              const aHasOrder = a.order !== null && a.order !== undefined && !isNaN(Number(a.order));
              const bHasOrder = b.order !== null && b.order !== undefined && !isNaN(Number(b.order));
              
              // If both items have valid order values, sort by order (ascending)
              if (aHasOrder && bHasOrder) {
                return Number(a.order) - Number(b.order);
              }
              // If only a has valid order, it comes first
              if (aHasOrder) {
                return -1;
              }
              // If only b has valid order, it comes first
              if (bHasOrder) {
                return 1;
              }
              // If neither has valid order, sort alphabetically by name
              return a.name.localeCompare(b.name);
            });
            
            if (categoryItems.length === 0) return null;
            
            return (
              <div 
                id={category._id}
                key={category._id}
                className="mb-20 last:mb-0 scroll-mt-36"
              >
                {/* Category and Menu Items Layout - Category on left, items on right */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Category Header - Left Column */}
                  <div>
                    <h2 className="text-3xl font-heading text-brand-navy uppercase">{category.name}</h2>
                    {category.description ? (
                      <div className="text-gray-700 text-sm mt-2 prose prose-sm max-w-none">
                        <PortableText value={category.description} />
                      </div>
                    ) : category.name.toLowerCase().includes("specialty") && (
                      <p className="text-gray-700 text-sm mt-2">
                        Each of these specialty pancake dishes are served a la carte unless noted otherwise. Ask your server about adding a side of meat or eggs for an additional charge.
                      </p>
                    )}
                  </div>
                  
                  {/* Menu Items Grid - Right Two Columns */}
                  <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 md:grid-flow-dense">
                    {categoryItems.map((item) => (
                      <div 
                        key={item._id}
                        className={`border border-brand-navy rounded-lg overflow-hidden ${item.featured ? 'featured-item' : ''}`}
                      >
                        <div 
                          onClick={(e) => {
                            e.preventDefault();
                            openMenuItemDrawer(item.slug, category.slug);
                          }}
                          className="block cursor-pointer"
                        >
                          {item.featured ? (
                            <div className="flex flex-col p-4">
                              {/* Featured Item Image - Takes up full width */}
                              <div className="w-full overflow-hidden rounded-md mb-4">
                                {item.imageUrl ? (
                                  <img 
                                    {...getSanityImageProps(item.imageUrl, 'menuCard')}
                                    alt={item.name} 
                                    className="w-full object-cover"
                                    style={{ aspectRatio: "16/9" }}
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              
                              {/* Featured Item Details */}
                              <div className="flex-1">
                                <div className="flex flex-col xl:flex-row xl:justify-between xl:items-start">
                                  <h3 className="font-bold text-brand-navy text-xl">{item.name}</h3>
                                  {item.calories && (
                                    <div className="text-xs text-gray-500 xl:ml-2 mt-1 xl:mt-0">{item.calories} cal</div>
                                  )}
                                </div>
                                {item.plainDescription && (
                                  <p className="text-sm text-gray-600 mt-2">{item.plainDescription}</p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex p-4">
                              {/* Regular Item Image */}
                              <div className="w-24 h-24 flex-shrink-0 overflow-hidden rounded-md">
                                {item.imageUrl ? (
                                  <img 
                                    {...getSanityImageProps(item.imageUrl, 'thumbnail')}
                                    alt={item.name} 
                                    className="w-full h-full object-cover"
                                    style={{ aspectRatio: "1/1" }}
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              
                              {/* Regular Item Details */}
                              <div className="ml-4 flex-1 flex flex-col justify-between">
                                <div>
                                  <div className="flex flex-col xl:flex-row xl:justify-between xl:items-start">
                                    <h3 className="font-bold text-brand-navy text-base">{item.name}</h3>
                                    {item.calories && (
                                      <div className="text-xs text-gray-500 xl:ml-2 mt-1 xl:mt-0">{item.calories} cal</div>
                                    )}
                                  </div>
                                  {item.plainDescription && (
                                    <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{item.plainDescription}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </Container>
      </div>
    </div>
  );
}